import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { engApi } from '../engApi';
import { VL, versionErrorText } from './digestVersionI18n';

// 有効化の確認 — 警告付きは了承チェック + 専用権限。expected_active_version_id で競合を検出（409 → 再読込を促す）
export function DigestActivateDialog({ sid, version, activeVersionId, activeVersionNumber, canWarn, lang, onClose, onDone }) {
  const L = VL[lang] || VL.ja;
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasWarn = version?.status === 'complete_with_warnings';
  const run = async () => {
    setBusy(true);
    try {
      const res = await engApi.activateDigestVersion(sid, version.id, {
        expected_active_version_id: activeVersionId || null, acknowledge_warnings: ack });
      toast.success(`${L.activated}: v${res.version_number}`);
      onDone(res);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code === 'ACTIVE_VERSION_CHANGED' ? L.raceChanged : versionErrorText(e, lang, L.activate));
      if (code === 'ACTIVE_VERSION_CHANGED') onDone(null);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!version} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="digest-activate-dialog">
        <DialogHeader><DialogTitle>{version ? L.activateTitle(version.version_number) : ''}</DialogTitle></DialogHeader>
        <p className="text-[13px] text-zinc-600">{L.activateBody(activeVersionNumber)}</p>
        {hasWarn && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900" data-testid="digest-activate-warning-box">
            {version?.user_warning && <p className="whitespace-pre-wrap mb-2">{version.user_warning}</p>}
            {canWarn ? (
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="digest-activate-ack" />
                <span>{L.ackWarnings}</span>
              </label>
            ) : <p className="font-semibold" data-testid="digest-activate-need-cap">{L.needWarnCap}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="digest-activate-cancel">{L.cancel}</Button>
          <Button onClick={run} disabled={busy || (hasWarn && (!canWarn || !ack))} className="bg-emerald-700 hover:bg-emerald-800" data-testid="digest-activate-ok">
            {L.activate}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DigestRestoreDialog({ sid, version, lang, onClose, onDone }) {
  const L = VL[lang] || VL.ja;
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await engApi.restoreDigestVersion(sid, version.id, {});
      toast.success(L.restored(res.version.version_number));
      onDone(res);
    } catch (e) {
      toast.error(versionErrorText(e, lang, L.restore));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!version} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="digest-restore-dialog">
        <DialogHeader><DialogTitle>{version ? L.restoreTitle(version.version_number) : ''}</DialogTitle></DialogHeader>
        <p className="text-[13px] text-zinc-600">{L.restoreBody}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="digest-restore-cancel">{L.cancel}</Button>
          <Button onClick={run} disabled={busy} className="bg-teal-700 hover:bg-teal-800" data-testid="digest-restore-ok">{L.confirm}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
